import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["scrollable", "loader", "endMessage", "state", "backToTop"]
  static values = {
    url: String,
    page: { type: Number, default: 1 },
    hasMore: { type: Boolean, default: true }
  }

  connect() {
    this.observer = new IntersectionObserver(
      entries => this.handleIntersection(entries),
      {
        root: null,
        rootMargin: "200px", // Start loading 200px before reaching the bottom
        threshold: 0
      }
    )

    // Show the loader initially so it can be observed
    if (this.hasLoaderTarget && this.hasMoreValue) {
      this.loaderTarget.classList.remove('hidden')
      this.observer.observe(this.loaderTarget)
    }

    if (!history.state || history.state.turbo != "restore") {
      window.scrollTo({ top: 0 })
    }

    if (this.hasBackToTopTarget) {
      this.backToTopTarget.style.opacity = "0"
      this.backToTopTarget.style.pointerEvents = "none"
    }

    this.handleScroll = this.toggleBackToTop.bind(this)
    window.addEventListener("scroll", this.handleScroll, { passive: true })
    this.toggleBackToTop()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }

    if (this.handleScroll) {
      window.removeEventListener("scroll", this.handleScroll)
    }
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting && this.hasMoreValue && !this.loading) {
        this.loadMore()
      }
    })
  }

  async loadMore() {
    if (this.loading || !this.hasMoreValue) return

    this.loading = true
    this.showLoader()

    const nextPage = this.pageValue + 1
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set('page', nextPage)

    // Preserve existing filters
    const currentParams = new URLSearchParams(window.location.search)
    currentParams.forEach((value, key) => {
      if (key !== 'page') {
        url.searchParams.set(key, value)
      }
    })

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const html = await response.text()

        // Check if we got any results
        if (html.trim() === '') {
          this.hasMoreValue = false
          this.showEndMessage()
        } else {
          // Let Turbo handle the stream response
          Turbo.renderStreamMessage(html)

          // Wait for DOM to update, then read the new state
          setTimeout(() => {
            this.updateStateFromElement()
          }, 100)
        }
      } else {
        console.error('Failed to load more items')
        this.hasMoreValue = false
      }
    } catch (error) {
      console.error('Error loading more items:', error)
      this.hasMoreValue = false
    } finally {
      this.loading = false
      this.hideLoader()
    }
  }

  showLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove('hidden')
    }
  }

  hideLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add('hidden')
    }
  }

  showEndMessage() {
    this.hideLoader()
    if (this.hasEndMessageTarget) {
      this.endMessageTarget.classList.remove('hidden')
    }
  }

  toggleBackToTop() {
    if (!this.hasBackToTopTarget) return

    const offset = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0

    if (offset > 200) {
      this.backToTopTarget.style.opacity = "1"
      this.backToTopTarget.style.pointerEvents = "auto"
    } else {
      this.backToTopTarget.style.opacity = "0"
      this.backToTopTarget.style.pointerEvents = "none"
    }
  }

  backToTop(event) {
    event.preventDefault()
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  // Called when filters change to reset the scroll state
  reset() {
    this.pageValue = 1
    this.hasMoreValue = true
    this.hideLoader()
    if (this.hasEndMessageTarget) {
      this.endMessageTarget.classList.add('hidden')
    }
  }

  // Update pagination state from the hidden state element
  updateStateFromElement() {
    if (this.hasStateTarget) {
      const page = parseInt(this.stateTarget.dataset.page)
      const hasMore = this.stateTarget.dataset.hasMore === 'true'

      this.pageValue = page
      this.hasMoreValue = hasMore

      if (!hasMore) {
        this.showEndMessage()
      } else {
        // Keep observing for more content
        this.showLoader()
      }
    }
  }
}
